import * as React from 'react';
import { UIHeading1, UIHeading2, UIHeading4 } from '@cian/ui-kit';

import { useTableScroll } from './hooks/useTableScroll';
import { HeaderRow } from './parts/HeaderRow';
import { ScrollButtons } from './parts/ScrollButtons';
import * as style from './style.css';

export interface INewbuildingsCompareBlockRow {
  kind: 'block';
  key: string;
  title: string;
}

export interface INewbuildingsCompareDataRow {
  kind: 'data';
  key: string;
  title?: string | null;
  cells: React.ReactNode[];
}

export type TNewbuildingsCompareViewRow = INewbuildingsCompareBlockRow | INewbuildingsCompareDataRow;

export interface INewbuildingsComparePageProps {
  title: string;
  headers: React.ReactNode[];
  floatingHeaders: React.ReactNode[];
  rows: TNewbuildingsCompareViewRow[];
}

export const NewbuildingsComparePage = ({ title, headers, floatingHeaders, rows }: INewbuildingsComparePageProps) => {
  const {
    scrollRef,
    floatingHeaderScrollRef,
    headerRowRef,
    dragging,
    isFloatingHeaderMounted,
    isFloatingHeaderVisible,
    scrollByColumn,
    scrollerProps,
  } = useTableScroll();

  return (
    <div className={style['container']} data-testid="newbuildings-compare_page">
      <div className={style['title']}>
        <UIHeading1 as="h1">{title}</UIHeading1>
        <ScrollButtons onScroll={scrollByColumn} />
      </div>
      {isFloatingHeaderMounted && (
        <div
          className={isFloatingHeaderVisible ? style['floating-header-visible'] : style['floating-header']}
          data-testid="FloatingHeader"
        >
          <div ref={floatingHeaderScrollRef} className={style['floating-header-scroller']}>
            <div className={style['table']} role="table">
              <HeaderRow floating headers={floatingHeaders} />
            </div>
          </div>
          <ScrollButtons floating onScroll={scrollByColumn} />
        </div>
      )}
      <div
        ref={scrollRef}
        className={dragging ? style['table-scroller-dragging'] : style['table-scroller']}
        data-testid="newbuildings-compare_table"
        role="region"
        tabIndex={0}
        {...scrollerProps}
      >
        <div className={style['table']} role="table">
          <HeaderRow headers={headers} rowRef={headerRowRef} />
          {rows.map(row =>
            row.kind === 'block' ? (
              <div className={style['row']} key={row.key} role="row">
                <div className={style['block-cell']} role="cell">
                  <UIHeading2>{row.title}</UIHeading2>
                </div>
              </div>
            ) : (
              <div className={style['row']} key={row.key} role="row">
                <div className={style['cell']} role="cell">
                  <UIHeading4>{row.title}</UIHeading4>
                </div>
                {row.cells.map((cell, cellIndex) => (
                  <div className={style['cell']} key={cellIndex} role="cell">
                    {cell}
                  </div>
                ))}
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
};
